import React, { useEffect, useState } from "react";
import axios from "axios";
import Modal from "react-modal";
import Select from "../../components/select";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { BsFiletypeCsv } from "react-icons/bs";

import useForm from "../../hooks/books/useForm";
import validate from "../../validation/bookValidationRules";
import PreviewImage from "../../components/image/previewImage";
import BulkUploadCategory from "./bulkUpload";
import SearchAuthor from "./searchAuthor";
import Button from "../../components/button";
import SummaryEditor from "../../components/textEditor/editor";
import { plainTextLength, isBlankHtml } from "../../helper";

import { useLanguage } from "../../context/languageContext";
import { usePermissions } from "../../context/permissionContext";
import { useAlreadyExists } from "../../components/confirmDialog";


const AddCategoryPopup = ({ isModalVisible, setIsAddCategoryModalVisible, closeModal, onSuccess }) => {
    const { submitLabel } = usePermissions();
  const { t } = useTranslation();
  const { selectedLang, adminInfo, setSelectedLang, fallbackLang } = useLanguage();
  const showAlreadyExists = useAlreadyExists();

  const [isBulkUploadBookPopupVisible, setIsBulkUploadBookPopupVisible] = useState(false);

  useEffect(() => {
    if (fallbackLang) {
      setSelectedLang(fallbackLang);
    }
  }, []);

  const handleAddCategory = async () => {
    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/books/add`, { values }, { headers });

      if (response.status === true) {
        onSuccess();
        closeModal();
        setValues({
          coverImage: {},
          publishedDate: "",
          numberOfPages: "",
          rating: null,
          link: "",
          translations: createInitialTranslationsArr()
        });
        toast.success(response.message);
        setIsSubmit(false);
      } else {
        toast.error(response.message);
        setIsSubmit(false);
      }
    } catch (error) {
      console.error("handleAddCategory catch error::", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      if (!showAlreadyExists(error)) toast.error(errorMessage);
    } finally {
      setIsSubmit(false);
    }
  };

  const {
    values,
    setValues,
    isLoading,
    setIsSubmit,
    isSubmit,
    errors,
    editMediaError,
    authorsData,
    allGenresOptions,
    dropdownRef,
    createInitialTranslationsArr,

    isShowAuthor,
    searchAuthorName,
    handleInputChange,
    handleAuthorSelect,
    handleRemoveAuthor,
    handleDropdownFocus,

    handleGetTranslationByLang,
    handleUpdateTranslationField,
    handleAddValues,
    handleUploadMedia,
    handleSubmit,
  } = useForm(handleAddCategory, validate, isModalVisible);

  const handleShowBulkUploadBookPopup = () => {
    setIsBulkUploadBookPopupVisible(true);
    setIsAddCategoryModalVisible(false);
  };

  const handleCloseBulkUploadBookPopup = () => {
    setIsBulkUploadBookPopupVisible(false);
  };


  return (
    <React.Fragment>
      <Modal
        isOpen={isModalVisible}
        onRequestClose={closeModal}
        className="modal_body_lg"
        overlayClassName="modal_overlay"
      >
        <div className="box_wrapper">
          <div className="d-flex justify-content-between fw-bold mb-3">
            <h3>Add a book</h3>
            <button type="button" className="btn btn-outline-dark" onClick={handleShowBulkUploadBookPopup}>Add Books in Bulk through CSV <BsFiletypeCsv /></button>
          </div>
          <div className="modal_content_lg">
            <div className="row mt-3">
              <div className="col-sm-12 col-md-6 mb-3">
                <label className="label">{t("book.authorName")}:</label>
                <SearchAuthor
                  searchAuthorName={searchAuthorName}
                  handleAuthorSelect={handleAuthorSelect}
                  authorsData={authorsData}
                  isShowAuthor={isShowAuthor}
                  handleInputChange={handleInputChange}
                  handleDropdownFocus={handleDropdownFocus}
                  handleRemoveAuthor={handleRemoveAuthor}
                  handleGetTranslationByLang={handleGetTranslationByLang}
                  selectedLang={selectedLang}
                  dropdownRef={dropdownRef}
                  isLoading={isLoading}
                />
                {errors && errors.author && <p className="error-message d-block">{errors.author}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <label className="label">{t("book.bookName")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.bookName")}...`}
                  type="text"
                  name="title"
                  value={handleGetTranslationByLang(selectedLang).title || ""}
                  onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                />
                {errors && errors.title && <p className="error-message d-block">{errors.title}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <label className="label">{t("book.tagline")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.tagline")}...`}
                  type="text"
                  name="tagline"
                  value={handleGetTranslationByLang(selectedLang).tagline || ""}
                  onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                />
                {/* {errors && errors.tagline && <p className="error-message d-block">{errors.tagline}</p>} */}
              </div>
              <div className="col-sm-12 col-md-3 mb-3">
                <label className="label">{t("book.publishedOn")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.publishedOn")}...`}
                  type="number"
                  name="publishedDate"
                  value={values.publishedDate}
                  // maxLength={4}
                  // pattern="\d*"  
                  onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                />
                {errors && errors.publishedDate && <p className="error-message d-block">{errors.publishedDate}</p>}
              </div>
              {/* <div className="col-sm-12 col-md-4 mb-3">
              <label className="label">{t("book.rating")}:</label>
              <input
                className="form_control"
                placeholder={`${t("general.enter")} ${t("book.rating")}...`}
                type="number"
                name="rating"
                onChange={(e) => handleAddValues(e.target.name, e.target.value)}
              />
              {/* {errors && errors.rating && <p className="error-message d-block">{errors.rating}</p>} */}
              {/* </div>  */}
              <div className="col-sm-12 col-md-3 mb-3">
                <label className="label">{t("book.numberOfPages")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.numberOfPages")}...`}
                  type="number"
                  name="numberOfPages"
                  value={values.numberOfPages || ""}
                  onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                />
                {errors && errors.numberOfPages && <p className="error-message d-block">{errors.numberOfPages}</p>}
              </div>
              <div className="col-sm-12 mb-3">
                <label className="label">{t("book.link")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.link")}...`}
                  type="text"
                  name="link"
                  value={values.link || ""}
                  onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                />
                {errors && errors.link && <p className="error-message d-block">{errors.link}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <div className="d-flex gap-2">
                  <label className="label">{t("book.genre")}:</label>
                  {["Fiction", "NonFiction"].map((genre) => (
                    <div key={genre} className="d-flex align-items-center">
                      <div className="form-label d-flex align-items-center text-muted">
                        {t(`general.${genre}`)}
                        <input
                          className="ms-2"
                          type="radio"
                          name="type"
                          value={genre}
                          checked={handleGetTranslationByLang(selectedLang).type === genre}
                          onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                          style={{ width: "18px", height: "18px" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <Select
                  className="form-control-md fw-medium p-0"
                  isMulti
                  closeMenuOnSelect={false}
                  name="genre"
                  value={handleGetTranslationByLang(selectedLang).genre?.map((item) => ({ label: item.genreName, value: item.genreId })) || []}
                  onChange={(selectedOptions) => {
                    handleUpdateTranslationField("genre", selectedOptions.map((option) => ({
                      genreName: option.label,
                      genreId: option.value,
                    })), selectedLang);
                  }}
                  options={allGenresOptions?.filter((option) => option.type === handleGetTranslationByLang(selectedLang).type)}
                />
                {errors && errors.genre && <p className="error-message d-block">{errors.genre}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <label className="label">{t("book.publishedBy")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.publishedBy")}...`}
                  type="text"
                  name="publishedBy"
                  value={handleGetTranslationByLang(selectedLang).publishedBy || ""}
                  onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                />
                {errors && errors.publishedBy && <p className="error-message d-block">{errors.publishedBy}</p>}
              </div>
              <div className="col-sm-12 col-md-8 mb-3">
                <label className="label">{t("book.aboutBook")}:
                  {" "}<span className="text-muted small fst-italic mt-1">(Please write about the book within given characters — {plainTextLength(handleGetTranslationByLang(selectedLang).summary)}/4500)</span>
                </label>
                <SummaryEditor
                  initialValue={handleGetTranslationByLang(selectedLang).summary || ""}
                  onChange={(content) =>
                    // Store "" rather than Quill's `<p><br></p>`, so an emptied
                    // field is genuinely empty downstream — validation, the
                    // book sync, and the app all read it as absent.
                    handleUpdateTranslationField("summary", isBlankHtml(content) ? "" : content, selectedLang)
                  }
                />
                {errors && errors.summary && (<p className="error-message d-block">{errors.summary}</p>)}
              </div>
              <div className="col-sm-12 col-md-4 mb-3">
                <PreviewImage
                  label={t("book.coverImage")}
                  image={values.coverImage?.uploadedImageUrl}
                  mediaError={editMediaError}
                  error={errors?.coverImage}
                  uploadMedia={(e) => handleUploadMedia(e)}
                />
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={isSubmit}>Cancel</button>
            <Button onClick={handleSubmit} disabled={isSubmit} isSubmit={isSubmit} label="Submitting ..." text={submitLabel("books")} />
          </div>
        </div>
      </Modal>

      <BulkUploadCategory
        isPopupVisible={isBulkUploadBookPopupVisible}
        handleClosePopup={handleCloseBulkUploadBookPopup}
        onSuccess={onSuccess}
      />
    </React.Fragment>
  );
};

export default AddCategoryPopup;
